import type { ActurisContactPolicy } from 'api/acturis/client-center';

export interface PolicyGroup {
  policyRef: string;
  versions: ActurisContactPolicy[];
}

export const policyType = (policy: ActurisContactPolicy) =>
  policy.product_target_detail || policy.product_name || 'Other';

export const policyStatuses = ({ versions }: PolicyGroup): string[] => {
  // A single card describes its returned version. Do not replace its Live status
  // with the conflicting LatestPolicyStatus returned by the UAT policy lookup.
  const statuses =
    versions.length === 1
      ? [
          versions[0].policy_status_detail ||
            versions[0].latest_policy_status_detail,
        ]
      : versions.some((version) => version.latest_policy_status_detail)
        ? versions.map((version) => version.latest_policy_status_detail)
        : versions.map((version) => version.policy_status_detail);
  return Array.from(
    new Set(
      statuses
        .filter(Boolean)
        .map((status) => (status === 'Live' ? 'Active' : status)),
    ),
  ) as string[];
};

export const groupPolicies = (
  policies: ActurisContactPolicy[],
): PolicyGroup[] => {
  const groups = new Map<string, PolicyGroup>();
  policies.forEach((policy) => {
    const group = groups.get(policy.policy_ref) || {
      policyRef: policy.policy_ref,
      versions: [],
    };
    if (
      !policy.version_ref ||
      !group.versions.some(
        (version) => version.version_ref === policy.version_ref,
      )
    ) {
      group.versions.push(policy);
    }
    groups.set(policy.policy_ref, group);
  });
  return Array.from(groups.values());
};

export const formatPolicyDate = (value?: string | null): string => {
  if (!value) return '—';
  // These are date-only API values; browser timezone must not shift the day.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
};
